import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Infinity as InfinityIcon, CalendarRange, GraduationCap, BookOpenText, Languages, Volume2, X, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  { icon: InfinityIcon, title: "Unlimited decrees", body: "Generate as many decree sets as you need, load more without limits, and decree any topic — private or shared to the Newsfeed.", to: "/generate", cta: "Open the Generator" },
  { icon: CalendarRange, title: "Full-length Decree Plans", body: "Build guided plans from 3 to 40 days and run several at once. Streaks and daily progress keep you established.", to: "/plans", cta: "Start a plan" },
  { icon: GraduationCap, title: "Every teaching", body: "Kingdom 101 and ICU Level 2 are fully open — every lesson, every section, with read-aloud audio.", to: "/teachings", cta: "Go to Teachings" },
  { icon: BookOpenText, title: "Unlimited word studies", body: "Search Strong's Concordance without daily limits and get AI word studies with every verse occurrence.", to: "/lexicon", cta: "Open the Lexicon" },
  { icon: Languages, title: "All 9 translations & highlights", body: "Read KJV, WEB, ASV, BSB, YLT, Darby, BBE, Webster and Douay-Rheims, and highlight verses in four colors synced to your account.", to: "/bible", cta: "Read the Bible" },
  { icon: Volume2, title: "Listen to everything", body: "Read-aloud is on for every decree set, daily decree, plan day and lesson — perfect for decreeing on the move.", to: "/daily", cta: "Hear today's decree" },
];

export function PremiumTour() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (user?.show_premium_tour) { setStep(0); setOpen(true); }
  }, [user?.show_premium_tour]);

  useEffect(() => {
    const handler = () => { setStep(0); setOpen(true); };
    window.addEventListener("iie:premium-tour", handler);
    return () => window.removeEventListener("iie:premium-tour", handler);
  }, []);

  if (!open || !user) return null;

  const finish = async (to) => {
    setOpen(false);
    if (user.show_premium_tour) {
      await api.post("/billing/tour-seen").catch(() => {});
      refreshUser();
    }
    if (to) navigate(to);
  };

  const s = STEPS[step];
  const Icon = s.icon;
  const last = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="premium-tour">
      <div className="absolute inset-0 bg-[#2C2A29]/60 backdrop-blur-[3px]" onClick={() => finish()} />
      <div className="relative w-full sm:max-w-md bg-[#FDFBF7] border border-[#D9D4CC] sm:rounded-sm shadow-2xl animate-fade-up overflow-hidden">
        <div className="h-1 bg-[#EAE6DF]">
          <div className="h-full bg-[#9B7E4E] transition-[width] duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
        <button data-testid="premium-tour-close" onClick={() => finish()} aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-sm text-[#7A756E] hover:bg-[#EAE6DF] transition-colors duration-300">
          <X className="w-5 h-5" />
        </button>
        <div className="px-7 sm:px-9 pt-8 pb-8">
          <div className="flex items-center gap-2.5">
            <Crown className="w-5 h-5 text-[#9B7E4E]" strokeWidth={1.5} />
            <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A756E]">Welcome to Premium · {step + 1} of {STEPS.length}</p>
          </div>
          <div key={step} className="animate-fade-up">
            <div className="w-14 h-14 mt-6 rounded-full bg-[#F3F0EA] flex items-center justify-center">
              <Icon className="w-7 h-7 text-[#9B7E4E]" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl text-[#2C2A29] tracking-tight mt-5" data-testid="premium-tour-title">{s.title}</h2>
            <p className="font-decree text-lg text-[#4A4744] mt-3 leading-relaxed">{s.body}</p>
          </div>
          <div className="mt-8 flex items-center justify-between gap-3">
            <button data-testid="premium-tour-try" onClick={() => finish(s.to)}
              className="font-body text-sm text-[#9B7E4E] underline underline-offset-4 hover:text-[#2C2A29] transition-colors duration-300">
              {s.cta}
            </button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button data-testid="premium-tour-back" onClick={() => setStep(step - 1)}
                  className="px-4 py-2.5 text-sm border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300">
                  Back
                </button>
              )}
              <button data-testid="premium-tour-next" onClick={() => (last ? finish() : setStep(step + 1))}
                className="flex items-center gap-2 px-5 py-2.5 text-sm bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300">
                {last ? "Start decreeing" : "Next"} {!last && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
